/**
 * Human-readable message from a CS API error (Phase 16 / ADR-026).
 *
 * The CS routers use two envelope shapes:
 *   - `{ error: 'code', message: 'text' }`          (tickets/routing/…)
 *   - `{ error: { code: 'code', message: 'text' } }` (agents/triage/…)
 * plus Zod `{ error: 'validation_failed', issues: [...] }`. This
 * flattens all three; unknown shapes fall back to the provided default.
 */
export function describeCsError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data && typeof data === 'object') {
      const envelope = data as {
        error?: unknown;
        message?: unknown;
        issues?: { message?: unknown; path?: unknown[] }[];
      };
      if (envelope.error && typeof envelope.error === 'object') {
        const nested = envelope.error as { message?: unknown };
        if (typeof nested.message === 'string') return nested.message;
      }
      if (typeof envelope.message === 'string') return envelope.message;
      if (Array.isArray(envelope.issues) && envelope.issues.length > 0) {
        const first = envelope.issues[0];
        if (first && typeof first.message === 'string') {
          const path = Array.isArray(first.path) ? first.path.join('.') : '';
          return path ? `${path}: ${first.message}` : first.message;
        }
      }
      if (typeof envelope.error === 'string') return `${fallback} (${envelope.error})`;
    }
  }
  return err instanceof Error ? err.message : fallback;
}
